import { useState } from 'react';
import { ExternalLink, FileText, Image, List, Paperclip } from 'lucide-react';
import { formatCurrency, sumMoney } from '@peles/shared';
import type { VendorPortalFile, VendorPortalItem, VendorPortalItemLine } from '@peles/shared';
import { useVendorEventItems } from '../../hooks/usePortal';
import { DocumentPreviewModal } from '../expenses/ExpenseDocumentsSection';
import type { PreviewableDocument } from '../documents/types';

interface VendorItemsPanelProps {
  eventId: string;
}

/**
 * The vendor portal's "Your items": each of the vendor's own booked lines on
 * the event, the items the office itemized it into with every detail, and the
 * files on the line and on each item. Read only — nothing here edits, uploads
 * or deletes; the server answers only with the `portalItems` grant.
 */
export function VendorItemsPanel({ eventId }: VendorItemsPanelProps) {
  const { data: lines, isLoading, isError } = useVendorEventItems(eventId);
  const [preview, setPreview] = useState<PreviewableDocument | null>(null);

  const heading = (
    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
      <List size={18} />
      Your items
    </h3>
  );

  if (isLoading)
    return (
      <div data-testid="vendor-items">
        {heading}
        <p className="text-sm text-gray-500">Loading items...</p>
      </div>
    );
  if (isError)
    return (
      <div data-testid="vendor-items">
        {heading}
        <p className="text-sm text-red-600">Your items could not be loaded.</p>
      </div>
    );

  return (
    <div data-testid="vendor-items">
      {heading}
      {!lines || lines.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing is booked with you on this event yet.</p>
      ) : (
        <div className="space-y-4">
          {lines.map((line) => (
            <ItemLineCard key={line.id} line={line} onOpenFile={setPreview} />
          ))}
        </div>
      )}
      {preview && <DocumentPreviewModal document={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function ItemLineCard({
  line,
  onOpenFile,
}: {
  line: VendorPortalItemLine;
  onOpenFile: (doc: PreviewableDocument) => void;
}) {
  const total = sumMoney(line.items.map((item) => item.total));
  return (
    <section
      className="rounded-lg border border-gray-200"
      data-testid={`vendor-item-line-${line.id}`}
      aria-label={line.itemName || line.vendorCategoryName || 'Booked line'}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 bg-gray-50 rounded-t-lg">
        <div>
          <p className="font-medium text-gray-900">
            {line.itemName || line.vendorCategoryName || 'Booked line'}
          </p>
          <p className="text-xs text-gray-500">
            {[line.vendorCategoryName, line.vendorName].filter(Boolean).join(' · ')}
          </p>
        </div>
        {line.items.length > 0 && (
          <span className="text-sm text-gray-700">
            {line.items.length} {line.items.length === 1 ? 'item' : 'items'} ·{' '}
            {formatCurrency(total)}
          </span>
        )}
      </header>

      <div className="p-3 space-y-3">
        {line.items.length === 0 ? (
          <p className="text-sm text-gray-500">No items listed for this line.</p>
        ) : (
          <>
            {/* A card per item on a phone, a table from `sm:` up. */}
            <ul className="space-y-2 sm:hidden">
              {line.items.map((item) => (
                <li
                  key={item.id}
                  className="rounded-md border border-gray-200 p-3 text-sm"
                  data-testid={`vendor-item-card-${item.id}`}
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{item.itemName || '—'}</span>
                    <span>{formatCurrency(item.total)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {quantityLabel(item)} × {formatCurrency(item.unitPrice)}
                    {item.roomName ? ` · ${item.roomName}` : ''}
                    {item.vendorCategoryName ? ` · ${item.vendorCategoryName}` : ''}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">{item.notes}</p>
                  )}
                  <FileList files={item.files} onOpen={onOpenFile} />
                </li>
              ))}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="p-2">Item</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Room</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Unit price</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2">Notes</th>
                    <th className="p-2">Files</th>
                  </tr>
                </thead>
                <tbody>
                  {line.items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-t align-top"
                      data-testid={`vendor-item-row-${item.id}`}
                    >
                      <td className="p-2 font-medium">{item.itemName || '—'}</td>
                      <td className="p-2">{item.vendorCategoryName || '—'}</td>
                      <td className="p-2">{item.roomName || '—'}</td>
                      <td className="p-2 text-right whitespace-nowrap">{quantityLabel(item)}</td>
                      <td className="p-2 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="p-2 text-right">{formatCurrency(item.total)}</td>
                      <td className="p-2 text-gray-600 whitespace-pre-line">{item.notes || '—'}</td>
                      <td className="p-2">
                        {item.files.length === 0 ? (
                          '—'
                        ) : (
                          <FileList files={item.files} onOpen={onOpenFile} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {line.files.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
              <Paperclip size={12} /> Files on this line
            </p>
            <FileList files={line.files} onOpen={onOpenFile} />
          </div>
        )}
      </div>
    </section>
  );
}

/** "3 each", "2", "1.5 hour" — the item's quantity with its unit. */
export function quantityLabel(item: Pick<VendorPortalItem, 'quantity' | 'unit'>): string {
  return item.unit ? `${item.quantity} ${item.unit}` : String(item.quantity);
}

/** A file's name, opened in the viewer when stored, or followed when it is a link. */
function FileList({
  files,
  onOpen,
}: {
  files: VendorPortalFile[];
  onOpen: (doc: PreviewableDocument) => void;
}) {
  if (files.length === 0) return null;
  return (
    <ul className="mt-1 space-y-1">
      {files.map((file) => {
        const name = file.fileName || 'File';
        const Icon = file.mimeType?.startsWith('image/') ? Image : FileText;
        if (!file.downloadUrl && file.externalUrl) {
          return (
            <li key={file.id}>
              <a
                href={file.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 sm:min-h-0 items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
              >
                <ExternalLink size={13} /> {name}
              </a>
            </li>
          );
        }
        return (
          <li key={file.id}>
            <button
              type="button"
              disabled={!file.downloadUrl}
              onClick={() =>
                file.downloadUrl &&
                onOpen({
                  id: file.id,
                  fileName: name,
                  mimeType: file.mimeType ?? 'application/octet-stream',
                  downloadUrl: file.downloadUrl,
                })
              }
              className="inline-flex min-h-11 sm:min-h-0 items-center gap-1.5 text-left text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400"
            >
              <Icon size={13} className="flex-shrink-0" />
              <span className="break-all">{name}</span>
              {file.type && <span className="text-xs text-gray-500">({file.type})</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
